import {
    ButtonGroup,
    Container,
    Flex,
    Heading,
    HStack,
    Td,
    Tr,
    IconButton,
    Box,
    Badge,
} from "@chakra-ui/react";
import { RepeatIcon } from "@chakra-ui/icons";
import { FaFileCsv } from "react-icons/fa";
import { Form, Table } from "components";
import { Moment } from "config";
import { useEffect, useState } from "react";
import usePurchaseClassificationList from "../../../hooks/usePurchaseClassificationList";

export default function PurchaseRating() {
    const initialPeriod = [Moment().subtract(3, "months").toDate(), Moment().toDate()];

    const [period, setPeriod] = useState(initialPeriod);
    const [thresholdMin, setThresholdMin] = useState("");
    const [thresholdMax, setThresholdMax] = useState("");
    const [orderBy, setOrderBy] = useState("approved_at");
    const [page, setPage] = useState(1);

    const validPeriod =
        period && period[0] instanceof Date && period[1] instanceof Date
            ? period
            : null;

    const thresholdMinBackend = thresholdMin ? parseInt(thresholdMin, 10) : 0;
    const thresholdMaxBackend = thresholdMax ? parseInt(thresholdMax, 10) : 0;

    const purchaseRatings = usePurchaseClassificationList({
        page,
        order_by: orderBy,
        period: validPeriod,
        threshold_min: thresholdMinBackend,
        threshold_max: thresholdMaxBackend,
    });

    useEffect(() => {
        setPage(1);
    }, [orderBy, thresholdMinBackend, thresholdMaxBackend, period]);

    const handlePeriodChange = (value) => setPeriod(value);

    const handleInputChange = (e, setter) => {
        const onlyDigits = e.target.value.replace(/\D/g, "");
        setter(onlyDigits);
    };

    const formatCurrency = (value) => {
        if (!value) return "";
        const number = parseInt(value, 10);
        return new Intl.NumberFormat("pt-BR", {
            style: "currency",
            currency: "BRL",
        }).format(number / 100);
    };

    const handleExportCsv = () => {
        if (!purchaseRatings.data?.data) return;

        const rows = purchaseRatings.data.data;
        const header = ["Núcleo/Distribuidor", "Total Compras"];

        const csvContent = [
            header.join(";"),
            ...rows.map((item) =>
                [item.distributor_name, item.total_compras].join(";")
            )
        ].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "relatorio_purchase_rating.csv";
        link.click();
    };

    const { meta } = purchaseRatings.data || {};

    return (
        <Container maxW="90%" mt={10}>
            <Flex justifyContent="space-between" alignItems="center">
                <Heading>Relatório - Classificação de Compras</Heading>
                <ButtonGroup size="xs">
                    <IconButton
                        icon={<RepeatIcon />}
                        aria-label="Recarregar"
                        isLoading={purchaseRatings.isFetching}
                        onClick={purchaseRatings.refetch}
                    />
                    {/*<IconButton*/}
                    {/*    icon={<FaFileCsv />}*/}
                    {/*    aria-label="Exportar CSV"*/}
                    {/*    onClick={handleExportCsv}*/}
                    {/*    isDisabled={*/}
                    {/*        purchaseRatings.isFetching ||*/}
                    {/*        !purchaseRatings.data?.data?.length*/}
                    {/*    }*/}
                    {/*    colorScheme="blue"*/}
                    {/*/>*/}
                </ButtonGroup>
            </Flex>

            {/* Filtros */}
            <HStack mt={4} spacing={2}>
                <Form.Text
                    fontSize="sm"
                    p={0} m={0}
                    pl={2}
                    placeholder="Limite mínimo"
                    value={formatCurrency(thresholdMin)}
                    onChange={(e) => handleInputChange(e, setThresholdMin)}
                />
                <Form.Text
                    fontSize="sm"
                    p={0} m={0}
                    pl={2}
                    placeholder="Limite máximo"
                    value={formatCurrency(thresholdMax)}
                    onChange={(e) => handleInputChange(e, setThresholdMax)}
                />
                <Form.Select
                    fontSize="sm"
                    p={0} m={0}
                    onChange={e => setOrderBy(e.target.value)}
                    placeholder="Ordenar por"
                    options={[
                        { label: "Data de aprovação", value: "approved_at" },
                        { label: "Data do pedido", value: "created_at" },
                        { label: "Total Compras", value: "total_compras" },
                    ]}
                />
                <Form.Interval initial={initialPeriod} onSelect={handlePeriodChange} />
            </HStack>

            {/* Tabela */}
            <Box mt={4}>
                {purchaseRatings.isLoading ? (
                    <Box borderRadius="md" overflow="hidden">
                        {[...Array(8)].map((_, row) => (
                            <Flex
                                key={row}
                                mb={2}
                                alignItems="center"
                                className="shimmer-row"
                                height="40px"
                            >
                                <Box
                                    flex="2"
                                    mr={2}
                                    borderRadius="md"
                                    className="shimmer"
                                />
                                <Box
                                    flex="1"
                                    borderRadius="md"
                                    className="shimmer"
                                />
                            </Flex>
                        ))}
                    </Box>
                ) : (
                    <Table
                        columns={[
                            { children: "Núcleo/Distribuidor" },
                            { children: "Total Compras", isNumeric: true },
                        ]}
                        setPage={setPage}
                        meta={meta}
                    >
                        {purchaseRatings.data?.data?.map((item, index) => (
                            <Tr
                                key={item.nucleo_id || index}
                                cursor="pointer"
                                _hover={{ backgroundColor: "rgba(0,0,0,0.03)" }}
                            >
                                <Td>{item.distributor_name}</Td>
                                <Td isNumeric>
                                    <Badge colorScheme="green" px={3} py={1} borderRadius="md">
                                        {item.total_compras_formatado}
                                    </Badge>
                                </Td>
                            </Tr>
                        ))}
                    </Table>
                )}
            </Box>

            {/* Shimmer CSS */}
            <style jsx>{`
                .shimmer {
                    height: 100%;
                    background: linear-gradient(
                            90deg,
                            #f0f0f0 25%,
                            #b7b6b6 50%,
                            #f0f0f0 75%
                    );
                    background-size: 200% 100%;
                    animation: shimmer 1.5s infinite;
                }

                @keyframes shimmer {
                    0% {
                        background-position: -200% 0;
                    }
                    100% {
                        background-position: 200% 0;
                    }
                }

                .shimmer-row {
                    display: flex;
                    gap: 8px;
                }
            `}</style>
        </Container>
    );
}
